import React from "react";
import { ArrowLeftIcon } from "@omega/icons";
import { Button, type ButtonProps, type PickerFullscreenRenderProps } from "@omega/ui";
import { renderOmpPickerFullscreen } from "./renderOmpPickerFullscreen";

/** API для кастомизации footer close в fullscreen Select/SearchSelect. */
export type FieldPickerFullscreenCloseApi = {
    /** Закрывает fullscreen overlay. */
    dismiss: () => void;
};

/**
 * Нижнее действие закрытия fullscreen для field-пикеров. \
 * `false` — скрыть footer. Render prop — полная замена дефолтной кнопки.
 * @param api.dismiss закрывает overlay
 */
export type FieldPickerFullscreenClose = false | ((api: FieldPickerFullscreenCloseApi) => React.ReactNode);

/** Пропсы дефолтной кнопки «Вернуться назад». */
export type FieldPickerFullscreenCloseButtonProps = Omit<ButtonProps, "onClick" | "children"> & {
    /** Текст кнопки. @default "Вернуться назад" */
    children?: React.ReactNode;
    /** Закрытие overlay (обычно из {@link FieldPickerFullscreenCloseApi.dismiss}). */
    onClick: () => void;
};

/**
 * Дефолтная кнопка выхода из fullscreen Select/SearchSelect: \
 * стрелка влево + «Вернуться назад». Экспортируется для композиции в `renderFullscreenClose`.
 * @example
 * ```tsx
 * renderFullscreenClose={({ dismiss }) => (
 *   <FieldPickerFullscreenCloseButton onClick={dismiss}>
 *     Назад к форме
 *   </FieldPickerFullscreenCloseButton>
 * )}
 * ```
 */
export function FieldPickerFullscreenCloseButton({
    children = "Вернуться назад",
    fullWidth = true,
    size = "md",
    startIcon = <ArrowLeftIcon />,
    tone = "accent",
    type = "button",
    variant = "solid",
    "aria-label": ariaLabel = "Закрыть список выбора и вернуться к форме",
    ...buttonProps
}: FieldPickerFullscreenCloseButtonProps) {
    return (
        <Button
            aria-label={ariaLabel}
            fullWidth={fullWidth}
            size={size}
            startIcon={startIcon}
            tone={tone}
            type={type}
            variant={variant}
            {...buttonProps}
        >
            {children}
        </Button>
    );
}

function resolveFullscreenClose(
    renderClose: FieldPickerFullscreenClose | undefined,
    dismiss: () => void,
): React.ReactNode | null {
    if (renderClose === false) {
        return null;
    }
    if (renderClose != null) {
        return renderClose({ dismiss });
    }
    return <FieldPickerFullscreenCloseButton onClick={dismiss} />;
}

/**
 * Собирает `renderFullscreen` для Select/SearchSelect: общий OMP shell + footer close.
 * @param renderClose кастомизация или `false`, чтобы скрыть кнопку
 * @returns renderer для `SelectField` / `SearchableSelectField` `renderFullscreen`
 */
export function createFieldPickerFullscreenRenderer(
    renderClose?: FieldPickerFullscreenClose,
): (props: PickerFullscreenRenderProps) => React.ReactNode {
    return (props) => {
        const dismiss = () => {
            props.dismiss("dismiss");
        };
        const footer = resolveFullscreenClose(renderClose, dismiss);
        return renderOmpPickerFullscreen(props, { footer });
    };
}
